import React from "react";
import { motion } from "framer-motion";

const TIERS = [
  {
    title: "Full Service Package",
    price: "from $140",
    sub: "Sedans · coupes · hatchbacks",
    items: [
      "Foam wash & paint decontamination",
      "Vacuum, shampoo, leather conditioning",
      "Wheels, tires, exterior trim",
      "Full interior refresh",
    ],
    featured: true,
  },
  {
    title: "Interior Reset",
    price: "from $90",
    sub: "High-quality interior refresh",
    items: [
      "Every surface cleaned & conditioned",
      "Deep vacuum & carpet shampoo",
      "Leather conditioning treatment",
      "Streak-free glass",
    ],
  },
  {
    title: "Exterior Car Wash",
    price: "from $70",
    sub: "Quick thorough exterior clean",
    items: [
      "pH-neutral foam wash",
      "Hand-dry & towel finish",
      "Wheel & tire cleaning",
      "Streak-free windows",
    ],
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="relative section"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-4xl mb-20 md:mb-28"
        >
          <p className="overline mb-6 text-gold-bright">Pricing</p>
          <h2 className="section-heading">
            Flat-rate.{" "}
            <br className="hidden sm:block" />
            <em
              className="italic"
              style={{
                fontFamily: "'Instrument Serif', 'Fraunces', Georgia, serif",
                background: "var(--gold-gradient)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "gold-shimmer 6s linear infinite",
              }}
            >
              Honest estimates.
            </em>
          </h2>
          <p
            className="text-lg leading-relaxed mt-8 max-w-2xl"
            style={{ letterSpacing: "-0.01em", color: "#b8b8c0" }}
          >
            $140 full detail. $90 interior reset. $70 exterior wash. +$20 for larger vehicles. Condition
            adjusts in your estimate before you confirm. No hidden fees.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.9,
                delay: i * 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              whileHover={{ y: -6, scale: 1.01 }}
              className="relative glass-card p-8 md:p-10 flex flex-col"
              style={{
                background: t.featured
                  ? "linear-gradient(160deg, rgba(35,28,12,0.85), rgba(15,12,4,0.85))"
                  : undefined,
                border: t.featured
                  ? "1px solid rgba(138,138,138,0.30)"
                  : undefined,
                boxShadow: t.featured
                  ? "0 32px 80px rgba(138,138,138,0.12), 0 4px 20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(170,170,170,0.06)"
                  : "0 24px 60px rgba(0,0,0,0.5)",
              }}
              data-testid={`pricing-tier-${i}`}
            >
              {t.featured && (
                <div
                  className="absolute top-6 right-6 text-[10px] tracking-[0.25em] uppercase font-mono px-3 py-1"
                  style={{
                    background: "rgba(138,138,138,0.18)",
                    border: "none",
                    borderRadius: "999px",
                    color: "#AAAAAA",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 0 16px rgba(138,138,138,0.10)",
                  }}
                >
                  Popular
                </div>
              )}

              <div className="mb-8">
                <h3
                  className="font-semibold tracking-[-0.02em] text-foreground"
                  style={{
                    fontSize: "clamp(1.2rem, 2vw, 1.5rem)",
                    color: t.featured ? "#AAAAAA" : undefined,
                  }}
                >
                  {t.title}
                </h3>
                <div
                  className="flex items-baseline gap-2 mt-3"
                  style={{ fontFamily: "monospace" }}
                >
                  <span
                    className="font-semibold tracking-tight"
                    style={{
                      fontSize: "clamp(1.6rem, 2.4vw, 2rem)",
                      color: t.featured ? "#8A8A8A" : "rgba(245,245,247,0.8)",
                    }}
                  >
                    {t.price}
                  </span>
                </div>
                <p
                  className="text-[11px] mt-3 tracking-[0.18em] uppercase"
                  style={{
                    color: t.featured
                      ? "rgba(170,170,170,0.7)"
                      : "rgba(245,245,247,0.4)",
                  }}
                >
                  {t.sub}
                </p>
              </div>

              <ul className="space-y-3.5 mt-auto">
                {t.items.map((it) => (
                  <li
                    key={it}
                    className="flex gap-3 text-[14.5px] leading-relaxed"
                    style={{
                      color: t.featured
                        ? "rgba(245,230,200,0.85)"
                        : "rgba(245,245,247,0.7)",
                    }}
                  >
                    <span
                      style={{
                        color: t.featured ? "#8A8A8A" : "rgba(138,138,138,0.6)",
                        flexShrink: 0,
                      }}
                    >
                      ✦
                    </span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center text-sm mt-10"
          style={{ letterSpacing: "-0.01em", color: "#b8b8c0" }}
        >
          All exterior services include a free ceramic coating upgrade with a 5-star Yelp review!
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center text-sm mt-16"
          style={{ letterSpacing: "-0.01em", color: "#b8b8c0" }}
        >
          Final price may adjust slightly on-site based on actual condition.{" "}
          <a
            href="/book"
            className="btn-link ml-1"
            style={{ display: "inline" }}
          >
            Get an instant estimate →
          </a>
        </motion.p>
      </div>
    </section>
  );
}
